import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import { Modal, Box, TextField, Stack, MenuItem, Switch, FormControlLabel, Alert } from "@mui/material";
import type { DialogProps } from "@mui/material";
import { 
    IAdvisorActivity, 
    IProject,
    AdvisorActivityInput 
} from "../../../types/database";

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    bgcolor: 'background.paper',
    border: '1px solid #c9c9c9',
    boxShadow: 24,
    p: 4,
    borderRadius: '10px',
    width: '50%'
};

interface ValidationError {
    validation: string;
    code: string;
    message: string;
    path: string[];
}

interface ApiError {
    message: string;
    errors?: ValidationError[];
}

interface AdvisorActivityFormProps {
    activity?: IAdvisorActivity;
    reloadActivities: () => Promise<void>;
    editMode: boolean;
    setEditMode: (mode: boolean) => void;
    projectId?: number;
    isOverride?: boolean;
    createActivity: (activity: AdvisorActivityInput) => Promise<{ success: boolean; data?: IAdvisorActivity; error?: any }>;
    updateActivity: (activityId: number, activity: Partial<AdvisorActivityInput>) => Promise<{ success: boolean; data?: IAdvisorActivity; error?: any }>;
}

export const AdvisorActivityForm = ({ 
    activity, 
    reloadActivities, 
    editMode, 
    setEditMode,
    projectId,
    isOverride,
    createActivity,
    updateActivity
}: AdvisorActivityFormProps) => {
    const { getAccessTokenSilently } = useAuth0();
    const [formError, setFormError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const initialActivity: AdvisorActivityInput = {
        key: activity?.key ?? "",
        name: activity?.name ?? "",
        description: activity?.description ?? "",
        default_share_value: activity?.default_share_value ?? 0,
        is_active: activity?.is_active ?? true,
        is_system_wide: !projectId,
        project: activity?.project ?? (projectId ? { id: projectId } as IProject : undefined),
        system_activity: activity?.system_activity
    };

    const [activityForm, setActivityForm] = useState(initialActivity);

    useEffect(() => {
        setActivityForm(initialActivity);
    }, [activity]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setFormError(null);
        setFieldErrors({});

        try {
            let success, error;
            if (activity?.id && !isOverride) {
                ({ success, error } = await updateActivity(activity.id, activityForm));
            } else {
                const { project, ...activityData } = activityForm;
                ({ success, error } = await createActivity(activityData));
            }
            if (!success) {
                const apiError = error as ApiError;
                if (apiError.errors) {
                    const newFieldErrors: Record<string, string> = {};
                    apiError.errors.forEach(err => {
                        if (err.path[0]) {
                            newFieldErrors[err.path[0]] = err.message;
                        }
                    });
                    setFieldErrors(newFieldErrors);
                }
                setFormError(apiError.message || 'An error occurred while saving the activity');
                return;
            }
            setActivityForm(initialActivity);
            await reloadActivities();
            setEditMode(false);
        } catch (error) {
            const apiError = error as ApiError;
            setFormError(apiError.message || 'An error occurred while saving the activity');
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.type === 'number' 
            ? parseFloat(event.target.value) 
            : event.target.type === 'checkbox'
                ? event.target.checked
                : event.target.value;
                
        setActivityForm({ ...activityForm, [event.target.name]: value });
    };

    const handleClose: DialogProps["onClose"] = (event, reason) => {
        if (reason && reason === "backdropClick") return;
        setEditMode(false);
    };

    const handleCancel = (event: React.MouseEvent) => {
        event.preventDefault();
        setActivityForm(initialActivity);
        setEditMode(false);
    };

    return (
        <Modal
            open={editMode}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <div className="admin-modal-intro">
                    <h5>
                        {isOverride ? "Create Project Override" : 
                         activity?.id ? "Edit Activity" : 
                         "Create New Activity"}
                    </h5>
                    <p>
                        {isOverride ? 
                            "Create a project-specific override of this system-wide activity." :
                            "Define an activity that advisors can earn shares for."}
                    </p>
                </div>
                {formError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {formError}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            name="key"
                            label="Activity Key"
                            value={activityForm.key}
                            onChange={handleChange}
                            required
                            fullWidth
                            disabled={!!activity?.id || isOverride}
                            error={!!fieldErrors.key}
                            helperText={fieldErrors.key || 
                                (activity?.id || isOverride ? 
                                    "Key cannot be changed after creation" : 
                                    "Unique identifier for this activity type (UPPER_SNAKE_CASE)")}
                        />

                        <TextField
                            name="name"
                            label="Activity Name"
                            value={activityForm.name}
                            onChange={handleChange}
                            required
                            fullWidth
                            error={!!fieldErrors.name}
                            helperText={fieldErrors.name}
                        />

                        <TextField
                            name="description"
                            label="Description"
                            value={activityForm.description}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            fullWidth
                            error={!!fieldErrors.description}
                            helperText={fieldErrors.description}
                        />

                        <TextField
                            name="default_share_value"
                            label="Default Share Value"
                            type="number"
                            value={activityForm.default_share_value}
                            onChange={handleChange}
                            required
                            inputProps={{ step: "0.01", min: "0" }}
                            fullWidth
                            error={!!fieldErrors.default_share_value}
                            helperText={fieldErrors.default_share_value}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    name="is_active"
                                    checked={activityForm.is_active}
                                    onChange={handleChange}
                                />
                            }
                            label="Active"
                        />

                        <Stack direction="row" spacing={2}>
                            <button type="submit" className="btn btn-primary">
                                {isOverride ? "Create Override" :
                                 activity?.id ? "Update" : 
                                 "Create"}
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </Stack>
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
}; 